import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ReviewCard from '@/components/feature/ReviewCard';
import { articles, categories } from '@/mocks/articles';
import { reviews } from '@/mocks/reviews';

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat?.name || slug;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <i className="ri-emotion-sad-line text-foreground-400 text-5xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-foreground-600 mb-6">你访问的页面不存在或已被删除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 4);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    url: `http://02office.cn/article/${article.slug}`,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    articleBody: article.content.replace(/<[^>]*>/g, ''),
    publisher: {
      '@type': 'Organization',
      name: '爆料网',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <section className="w-full bg-background-50 pt-20 pb-8">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row items-start gap-10 mb-8">
            <div className="flex-1">
              <h1 className="text-3xl md:text-5xl font-black text-foreground-950 font-heading leading-tight">
                {article.title}
              </h1>
            </div>
            <div className="flex-1 flex flex-col gap-4">
              <p className="text-foreground-600 text-sm leading-relaxed">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span>{article.publishDate}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.viewCount > 10000
                    ? `${(article.viewCount / 10000).toFixed(1)}万阅读`
                    : `${article.viewCount}阅读`}
                </span>
              </div>
              <Link
                to="/"
                className="text-primary-500 hover:text-primary-400 font-semibold text-sm flex items-center gap-1.5 transition-colors duration-200 group cursor-pointer"
              >
                返回列表
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200"></i>
              </Link>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden h-[280px] md:h-[400px]">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 爆料网`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-10">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-1/3 shrink-0">
              <div className="sticky top-24">
                <div className="inline-flex items-center gap-2 border border-primary-500/40 rounded-full px-4 py-1.5 mb-5">
                  <span className="text-primary-500 text-xs font-semibold">独家撰稿</span>
                </div>
                <div className="bg-background-200 rounded-2xl overflow-hidden mb-5">
                  <img
                    src={article.imageUrl}
                    alt={article.author}
                    className="w-full h-56 object-cover object-top"
                  />
                </div>
                <div className="flex flex-wrap gap-2 mb-5">
                  <Link
                    to={`/category/${article.category}`}
                    className="bg-primary-500/10 text-primary-400 text-xs font-medium px-3 py-1.5 rounded-full hover:bg-primary-500/20 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {getCategoryName(article.category)}
                  </Link>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/tag/${tag}`}
                      className="bg-background-200 text-foreground-600 text-xs px-3 py-1.5 rounded-full hover:bg-background-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      # {tag}
                    </Link>
                  ))}
                </div>
                <button className="bg-primary-500/10 hover:bg-primary-500/20 text-primary-400 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2">
                  <i className="ri-user-add-line"></i>
                  关注作者
                </button>
              </div>
            </aside>

            <article className="lg:w-2/3">
              <div
                className="prose-custom text-foreground-700 text-sm leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />
            </article>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-2 h-2 rounded-full bg-primary-500"></div>
            <span className="text-primary-500 text-xs font-semibold">网友热评</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading mb-2">
            读者怎么看
          </h2>
          <p className="text-foreground-600 text-sm mb-8">全网口碑评价</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((review, index) => (
              <ReviewCard
                key={review.id}
                {...review}
                faded={index === reviews.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {relatedArticles.length > 0 && (
        <section className="w-full bg-background-50 py-14">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-3xl font-bold text-foreground-950 font-heading mb-2">
              相关推荐
            </h2>
            <p className="text-foreground-600 text-sm mb-8">
              更多{getCategoryName(article.category)}相关爆料
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {relatedArticles.map((ra) => (
                <ArticleCard
                  key={ra.id}
                  slug={ra.slug}
                  title={ra.title}
                  excerpt={ra.excerpt}
                  category={ra.category}
                  categoryName={getCategoryName(ra.category)}
                  publishDate={ra.publishDate}
                  imageUrl={ra.imageUrl}
                  isHot={ra.isHot}
                  isExclusive={ra.isExclusive}
                  viewCount={ra.viewCount}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}
